import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import PortfolioMainContent from "./PortfolioMainContent";
import PortfolioTable from "./PortfolioTableOptimized";
import PositionsSummaryPopUp from "./Popup/PositionsSummaryPopUp";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { requestMarketData } from "../../Redux/Reducers/marketDataSlice";
import Loader from "../../component/Loader";
import { usePortfolioLayoutState } from "../../hooks/useOptimizedState";
import { downloadPortfolioExport } from "./utils/portfolioExport";
import {
  extraPortfolioBenchmarksFor,
} from "../../utils/portfolioBenchmarks";

const PortfolioDetails = ({ fundName, strategyName, exportRef }) => {
  const dispatch = useDispatch();
  const exportSnapshotRef = useRef({});

  const updateExportSnapshot = useCallback((partial) => {
    exportSnapshotRef.current = { ...exportSnapshotRef.current, ...partial };
  }, []);

  const handleExportDataChange = useCallback(
    (partial) => {
      updateExportSnapshot(partial);
    },
    [updateExportSnapshot]
  );

  useEffect(() => {
    if (!exportRef) return;
    exportRef.current = () => downloadPortfolioExport(exportSnapshotRef.current);
  }, [exportRef]);

  // Use optimized hook to prevent unnecessary re-renders
  const {
    strategies,
    threshold,
    lots,
    trades,
    isStrategist,
    RawMarketData,
    livePrices,
  } = usePortfolioLayoutState();
  const { user, userInfo } = useSelector((state) => state?.users);
  const extraBenchmarks = extraPortfolioBenchmarksFor(user, userInfo);

  const [totals, setTotals] = useState([]);
  const timeoutRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isViewBenchmarkData, setisViewBenchmarkData] = useState(false);
  const [isGridViewSummary, setIsGridViewSummary] = useState(false);
  const [isPositionPerformance, setIsPositionPerformance] = useState(false);
  const [value, setValue] = React.useState(0);
  const [isMarketData, setIsMarketData] = useState(false);
  const [dataFetching, setDataFetching] = useState(false);

  // Memoize static array to prevent recreation
  const longShortLabels = useMemo(
    () => ["", "%AUM", "P(L)", "%P(L)", "Signals PnL"],
    []
  );

  // Memoize strategy name logic
  const finalStrategyName = useMemo(() => {
    if (isStrategist && strategies?.length > 0) {
      return strategies[0].name;
    }
    return strategyName;
  }, [isStrategist, strategies, strategyName]);

  // Memoize title text
  const titleText = useMemo(() => {
    return isStrategist
      ? "Strategy Performance Summary"
      : "Funds Performance Summary";
  }, [isStrategist]);

  useEffect(() => {
    updateExportSnapshot({
      totals,
      titleText,
      longShortLabels,
      livePrices,
      extraBenchmarks,
    });
  }, [totals, titleText, longShortLabels, livePrices, extraBenchmarks, updateExportSnapshot]);

  // Optimize event handlers with useCallback
  const toggleGridViewSummary = useCallback(() => {
    setIsGridViewSummary((prev) => !prev);
  }, []);

  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);
  }, []);

  const handlePositionPerformanceClick = useCallback(() => {
    setIsPositionPerformance(true);
  }, []);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setDataFetching(true);
    // Set a new timeout to trigger the function after 20 seconds
    timeoutRef.current = setTimeout(() => {
      setIsMarketData(false);
      dispatch(
        requestMarketData({
          lots: lots,
          threshold: threshold,
          noRequest: true,
          trades: trades,
        })
      );
    }, 20000); // 20 seconds

    // Cleanup the timeout if the component unmounts or RawMarketData changes
    return () => clearTimeout(timeoutRef.current);
  }, [RawMarketData, dispatch, lots, threshold, trades]);

  return (
    <>
      {isMarketData ? (
        <Loader msg={true} marketLoader={true} />
      ) : (
        <>
          <PortfolioMainContent
            titleText={titleText}
            longShortLabels={longShortLabels}
            totals={totals}
            onToggleGridViewSummary={toggleGridViewSummary}
            value={value}
            onChange={handleChange}
            onPositionPerformanceClick={handlePositionPerformanceClick}
            livePrices={livePrices}
          />

          <Box sx={{ mb: 3 }}>
            <PortfolioTable
              fundname={fundName}
              strategyname={finalStrategyName}
              setTotals={setTotals}
              threshold={threshold}
              onExportDataChange={handleExportDataChange}
            />
          </Box>
          <PositionsSummaryPopUp
            openSummaryPopup={isGridViewSummary}
            setopenSummaryPopup={setIsGridViewSummary}
            totals={totals}
            tablehead={longShortLabels}
            title={titleText}
          />
        </>
      )}
    </>
  );
};

export default PortfolioDetails;
